/**
 * Bandwidth-Constrained Sync Engine
 * 
 * Implements the 3-phase lightweight sync protocol with strict
 * bandwidth constraints. Default: 25 MB/day (adjustable in settings).
 * 
 * Features:
 * - Bandwidth tracking (daily limits)
 * - Intelligent phase selection
 * - Priority-based data sync
 * - Automatic throttling when near limits
 * - Adjustable settings
 */

import { upsertData } from '../services/dataSourceService';
import { db } from '../database/db';
import { MerkleTreeBuilder, treeCache, MerkleTree } from './merkle-tree';
import { createLogger } from '../utils/logger';

const logger = createLogger('BandwidthSyncEngine');

export interface BandwidthSettings {
  dailyLimitMB: number;        // Default: 25 MB
  enableAtSeaMode: boolean;    // When enabled, strict limits apply
  syncPriority: 'critical' | 'balanced' | 'maximum';
  autoSync: boolean;           // Background sync enabled
  autoSyncInterval: number;    // Milliseconds
}

export interface BandwidthUsage {
  date: Date;
  phaseBytes: {
    phase1: number;            // Root hash exchange
    phase2: number;            // Branch hash exchange
    phase3: number;            // Data sync
  };
  totalBytes: number;
  tables: {
    [table: string]: {
      bytes: number;
      recordsSynced: number;
    };
  };
}

export interface SyncSession {
  id: string;
  startedAt: Date;
  tables: string[];
  phase: 1 | 2 | 3;
  status: 'running' | 'paused' | 'complete' | 'failed';
  bytesUsed: number;
  bytesRemaining: number;
  estimatedCompletion: Date | null;
}

/**
 * Global Bandwidth Manager
 */
export class BandwidthManager {
  private static settings: BandwidthSettings = {
    dailyLimitMB: 25,
    enableAtSeaMode: false,
    syncPriority: 'balanced',
    autoSync: true,
    autoSyncInterval: 300000, // 5 minutes
  };

  private static usageToday: BandwidthUsage = {
    date: new Date(),
    phaseBytes: { phase1: 0, phase2: 0, phase3: 0 },
    totalBytes: 0,
    tables: {},
  };

  /**
   * Initialize bandwidth manager
   */
  static async initialize(customSettings?: Partial<BandwidthSettings>): Promise<void> {
    if (customSettings) {
      this.settings = { ...this.settings, ...customSettings };
    }

    // Load usage from storage
    await this.loadTodaysUsage();

    logger.info('Bandwidth Manager initialized', {
      dailyLimitMB: this.settings.dailyLimitMB,
      atSeaMode: this.settings.enableAtSeaMode,
      priority: this.settings.syncPriority
    });
  }

  /**
   * Get current settings
   */
  static getSettings(): BandwidthSettings {
    return { ...this.settings };
  }

  /**
   * Update settings
   */
  static updateSettings(updates: Partial<BandwidthSettings>): void {
    this.settings = { ...this.settings, ...updates };
    this.saveSettings();

    logger.info('Bandwidth settings updated', updates);
  }

  /**
   * Get remaining bandwidth for today
   */
  static getRemainingBandwidth(): number {
    const limitBytes = this.settings.dailyLimitMB * 1024 * 1024;
    return Math.max(0, limitBytes - this.usageToday.totalBytes);
  }

  /**
   * Get remaining bytes (alias for getRemainingBandwidth)
   */
  static getRemainingBytes(): number {
    return this.getRemainingBandwidth();
  }

  /**
   * Get bandwidth usage percentage
   */
  static getUsagePercent(): number {
    const limitBytes = this.settings.dailyLimitMB * 1024 * 1024;
    return Math.round((this.usageToday.totalBytes / limitBytes) * 100);
  }

  /**
   * Check if bandwidth available for operation
   */
  static canProceed(bytes: number): boolean {
    const remaining = this.getRemainingBandwidth();
    return bytes <= remaining;
  }

  /**
   * Check if sync can proceed with given bytes
   */
  static canSync(bytes: number): boolean {
    return this.canProceed(bytes);
  }

  /**
   * Record bandwidth usage
   * Supports multiple signatures:
   * - recordUsage(bytes: number) - records to totalBytes only (no phase)
   * - recordUsage(phase: 1 | 2 | 3, bytes: number, table?: string)
   * - recordUsage(bytes: number, phase?: 'phase1' | 'phase2' | 'phase3')
   */
  static recordUsage(
    phaseOrBytes: 1 | 2 | 3 | number,
    bytesOrPhase?: number | 'phase1' | 'phase2' | 'phase3',
    table?: string
  ): void {
    let phase: 1 | 2 | 3 | undefined;
    let bytes: number;

    // Handle overloaded signatures
    if (typeof phaseOrBytes === 'number' && typeof bytesOrPhase === 'string') {
      // Signature: recordUsage(bytes, 'phase1' | 'phase2' | 'phase3')
      bytes = phaseOrBytes;
      const phaseStr = bytesOrPhase.replace('phase', '');
      phase = parseInt(phaseStr) as 1 | 2 | 3;
    } else if (typeof phaseOrBytes === 'number' && typeof bytesOrPhase === 'number') {
      // Signature: recordUsage(bytes, phase) - backwards compatibility
      bytes = phaseOrBytes;
      phase = bytesOrPhase as 1 | 2 | 3;
    } else if (typeof phaseOrBytes === 'number' && (phaseOrBytes === 1 || phaseOrBytes === 2 || phaseOrBytes === 3) && typeof bytesOrPhase === 'number') {
      // Signature: recordUsage(phase, bytes, table?)
      phase = phaseOrBytes;
      bytes = bytesOrPhase;
    } else if (typeof phaseOrBytes === 'number' && bytesOrPhase === undefined) {
      // Signature: recordUsage(bytes) - no phase specified, just record total
      bytes = phaseOrBytes;
      phase = undefined;
    } else {
      // Fallback: try to interpret as (phase, bytes)
      if (typeof phaseOrBytes === 'number' && (phaseOrBytes === 1 || phaseOrBytes === 2 || phaseOrBytes === 3)) {
        phase = phaseOrBytes;
        bytes = bytesOrPhase as number;
      } else {
        // Invalid signature
        logger.error('Invalid recordUsage signature', { phaseOrBytes, bytesOrPhase });
        return;
      }
    }

    // Ensure bytes is a number
    bytes = Number(bytes);
    if (isNaN(bytes) || bytes < 0) {
      logger.error('Invalid bytes value', { bytes });
      return;
    }

    // Only update phase-specific bytes if phase is specified
    if (phase !== undefined) {
      this.usageToday.phaseBytes[`phase${phase}` as keyof typeof this.usageToday.phaseBytes] += bytes;
    }
    this.usageToday.totalBytes += bytes;

    if (table) {
      if (!this.usageToday.tables[table]) {
        this.usageToday.tables[table] = { bytes: 0, recordsSynced: 0 };
      }
      this.usageToday.tables[table].bytes += bytes;
    }

    // Save usage to storage
    this.saveTodaysUsage();

    const percentUsed = this.getUsagePercent();
    logger.info('Bandwidth used', {
      phase,
      bytes,
      totalBytes: this.usageToday.totalBytes,
      percentUsed
    });

    // Warn if approaching limit
    if (percentUsed > 80 && percentUsed <= 85) {
      logger.warn('Bandwidth 80% used', {
        used: this.usageToday.totalBytes,
        limit: this.settings.dailyLimitMB * 1024 * 1024
      });
    } else if (percentUsed > 95) {
      logger.error('Bandwidth 95%+ used - syncs will be throttled');
    }
  }

  /**
   * Get today's usage
   */
  static getUsageToday(): BandwidthUsage {
    return {
      date: new Date(this.usageToday.date),
      phaseBytes: {
        phase1: this.usageToday.phaseBytes.phase1,
        phase2: this.usageToday.phaseBytes.phase2,
        phase3: this.usageToday.phaseBytes.phase3,
      },
      totalBytes: this.usageToday.totalBytes,
      tables: { ...this.usageToday.tables },
    };
  }

  /**
   * Calculate throttle delay based on current usage
   * Returns delay in milliseconds
   */
  static calculateThrottleDelay(): number {
    const percentUsed = this.getUsagePercent();

    // No delay if under 50% usage
    if (percentUsed < 50) {
      return 0;
    }

    // Progressive delay as usage increases
    if (percentUsed >= 95) {
      return 60000; // 1 minute delay at 95%+
    } else if (percentUsed >= 80) {
      return 30000; // 30 seconds at 80%+
    } else if (percentUsed >= 70) {
      return 15000; // 15 seconds at 70%+
    } else {
      // Linear scaling between 50-70%
      const scale = (percentUsed - 50) / 20; // 0 to 1
      return Math.round(scale * 15000); // 0 to 15 seconds
    }
  }

  /**
   * Reset daily usage (for new day)
   */
  private static resetDailyUsage(): void {
    const today = new Date();
    if (this.usageToday.date.toDateString() !== today.toDateString()) {
      this.usageToday = {
        date: today,
        phaseBytes: { phase1: 0, phase2: 0, phase3: 0 },
        totalBytes: 0,
        tables: {},
      };
      logger.info('Daily bandwidth usage reset');
    }
  }

  /**
   * Force reset usage (for testing)
   */
  static resetUsage(): void {
    this.usageToday = {
      date: new Date(),
      phaseBytes: { phase1: 0, phase2: 0, phase3: 0 },
      totalBytes: 0,
      tables: {},
    };
    this.saveTodaysUsage();
  }

  /**
   * Save settings to storage
   */
  private static saveSettings(): void {
    try {
      localStorage.setItem('bandwidth_settings', JSON.stringify(this.settings));
    } catch (error) {
      logger.error('Failed to save bandwidth settings', error);
    }
  }


  /**
   * Save today's usage to storage
   */
  private static saveTodaysUsage(): void {
    try {
      localStorage.setItem('bandwidth_usage_today', JSON.stringify(this.usageToday));
    } catch (error) {
      logger.error('Failed to save bandwidth usage', error);
    }
  }

  /**
   * Load today's usage from storage
   */
  private static async loadTodaysUsage(): Promise<void> {
    try {
      const saved = localStorage.getItem('bandwidth_usage_today');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Check if it's from today
        const savedDate = new Date(parsed.date);
        const today = new Date();

        if (savedDate.toDateString() === today.toDateString()) {
          // Ensure numeric values (localStorage may store as strings)
          this.usageToday = {
            date: new Date(parsed.date),
            phaseBytes: {
              phase1: Number(parsed.phaseBytes?.phase1 || 0),
              phase2: Number(parsed.phaseBytes?.phase2 || 0),
              phase3: Number(parsed.phaseBytes?.phase3 || 0),
            },
            totalBytes: Number(parsed.totalBytes || 0),
            tables: parsed.tables || {},
          };
        } else {
          // New day, reset
          this.resetDailyUsage();
        }
      }
    } catch (error) {
      logger.error('Failed to load bandwidth usage', error);
      this.resetDailyUsage();
    }
  }
}

/**
 * 3-Phase Sync Engine with Bandwidth Constraints
 */
export class BandwidthConstrainedSync {
  private static readonly HASH_SIZE = 32;               // SHA-256 = 32 bytes

  /**
   * Phase 1: Exchange Root Hashes
   * Bandwidth: ~32 bytes per table
   */
  static async phase1_exchangeRoots(
    tables: string[]
  ): Promise<{
    rootHashes: Map<string, string>;
    bytesUsed: number;
    tablesProcessed: number;
  }> {
    logger.info('Phase 1: Exchanging root hashes', { tableCount: tables.length });

    const rootHashes = new Map<string, string>();
    let bytesUsed = 0;
    let tablesProcessed = 0;

    for (const table of tables) {
      // Check bandwidth
      const estimatedBytes = this.HASH_SIZE + 32; // Hash + overhead
      if (!BandwidthManager.canProceed(estimatedBytes)) {
        logger.warn('Bandwidth limit reached during Phase 1');
        break;
      }

      try {
        // Try cache first
        let tree = treeCache.get(table);

        // Build if not cached
        if (!tree) {
          const records = await (db as any)[table].toArray();
          if (records.length > 0) {
            tree = await MerkleTreeBuilder.buildTree(records, table);
            treeCache.set(table, tree);
          } else {
            logger.info('Table is empty, skipping', { table });
            continue;
          }
        }

        rootHashes.set(table, tree.hash);
        bytesUsed += this.HASH_SIZE;
        tablesProcessed++;

        logger.info('Root hash exchanged', { table, hashPreview: tree.hash.substring(0, 16) });
      } catch (error) {
        logger.error('Phase 1 failed for table', { table, error });
      }
    }

    BandwidthManager.recordUsage(1, bytesUsed);

    logger.info('Phase 1 complete', { tablesProcessed, bytesUsed });

    return { rootHashes, bytesUsed, tablesProcessed };
  }

  /**
   * Phase 2: Exchange Branch Hashes (Delta Detection)
   * Bandwidth: ~64-256 bytes per table
   */
  static async phase2_exchangeBranches(
    localTrees: Map<string, MerkleTree>,
    remoteRoots: Map<string, string>
  ): Promise<{
    changedTables: Map<string, string[]>;
    bytesUsed: number;
    bytesRequired: number;
  }> {
    logger.info('Phase 2: Exchanging branch hashes for delta detection');

    const changedTables = new Map<string, string[]>();
    let bytesUsed = 0;
    let bytesRequired = 0;

    for (const [table, localTree] of localTrees) {
      const remoteRoot = remoteRoots.get(table);
      if (!remoteRoot) continue;

      // Check if roots match
      if (localTree.hash === remoteRoot) {
        logger.info('No changes detected (roots match)', { table });
        continue;
      }

      // Roots differ - get branch hashes
      const branchHashes = MerkleTreeBuilder.getBranchHashes(localTree, 1);
      const branchBytesNeeded = branchHashes.length * this.HASH_SIZE;

      // Check bandwidth
      if (!BandwidthManager.canProceed(branchBytesNeeded)) {
        logger.warn('Bandwidth limit reached during Phase 2', { table });
        bytesRequired += branchBytesNeeded;
        continue;
      }

      changedTables.set(table, branchHashes);
      bytesUsed += branchBytesNeeded;
      bytesRequired += branchBytesNeeded;

      logger.warn('Changes detected', { table, branchCount: branchHashes.length, bytes: branchBytesNeeded });
    }

    BandwidthManager.recordUsage(2, bytesUsed);

    logger.info('Phase 2 complete', {
      changedTables: changedTables.size,
      bytesUsed,
      bytesRequired
    });

    return { changedTables, bytesUsed, bytesRequired };
  }

  /**
   * Phase 3: Sync Changed Records
   * Bandwidth: Only changed data
   */
  static async phase3_syncChangedRecords(
    table: string,
    changedRecordIds: string[],
    organizationId?: string
  ): Promise<{
    recordsSynced: number;
    bytesUsed: number;
    bytesSynced: number;
  }> {
    logger.info('Phase 3: Syncing changed records', { table, recordCount: changedRecordIds.length });

    let recordsSynced = 0;
    let bytesUsed = 0;
    const skippedRecords: string[] = [];

    for (const recordId of changedRecordIds) {
      try {
        const record = await (db as any)[table].get(recordId);
        if (!record) continue;

        const recordBytes = JSON.stringify(record).length;

        // Check bandwidth before sending each record
        if (!BandwidthManager.canProceed(recordBytes)) {
          logger.warn('Bandwidth limit reached - stopping Phase 3', { table });
          skippedRecords.push(recordId);
          continue;
        }

        // Push to Supabase via service; require organizationId for multi-tenancy safety
        let error: Error | null = null;
        if (!organizationId) {
          logger.error('Missing organizationId for upsert; skipping record to enforce multi-tenancy');
          skippedRecords.push(recordId);
          continue;
        }
        const res = await upsertData(table, 'supabase', record, organizationId);
        error = res.error;

        if (error) {
          logger.error('Failed to sync record', { table, recordId, error });
          skippedRecords.push(recordId);
          continue;
        }

        bytesUsed += recordBytes;
        recordsSynced++;

        if (recordsSynced % 10 === 0) {
          const percent = Math.round(
            ((recordsSynced) / changedRecordIds.length) * 100
          );
          logger.info('Progress', { percent, synced: recordsSynced, total: changedRecordIds.length });
        }
      } catch (error) {
        logger.error('Phase 3 error for record', { recordId, error });
        skippedRecords.push(recordId);
      }
    }

    BandwidthManager.recordUsage(3, bytesUsed, table);

    logger.info('Phase 3 complete', {
      synced: recordsSynced,
      total: changedRecordIds.length,
      bytesUsed
    });
    
    if (skippedRecords.length > 0) {
      logger.warn('Skipped records due to bandwidth limits', { count: skippedRecords.length });
    }

    return {
      recordsSynced,
      bytesUsed,
      bytesSynced: bytesUsed,
    };
  }

  /**
   * Full sync with bandwidth constraints
   * TODO: Add orgId and vesselId parameters when implementing server-side sync
   */
  static async fullSync(
    tables: string[],
    options?: { organizationId?: string }
  ): Promise<{
    tablesProcessed: number;
    recordsSynced: number;
    totalBytesUsed: number;
    remainingBandwidth: number;
  }> {
    logger.info('Starting full sync', { tableCount: tables.length });
    const startTime = performance.now();

    let totalBytesUsed = 0;
    let recordsSynced = 0;
    let tablesProcessed = 0;

    try {
      // Phase 1: Root hashes
      const phase1 = await this.phase1_exchangeRoots(tables);
      totalBytesUsed += phase1.bytesUsed;
      tablesProcessed = phase1.tablesProcessed;

      // Check if we should continue
      if (!BandwidthManager.canProceed(100 * 1024)) {
        logger.warn('Insufficient bandwidth for Phase 2');
        return {
          tablesProcessed,
          recordsSynced,
          totalBytesUsed,
          remainingBandwidth: BandwidthManager.getRemainingBandwidth(),
        };
      }

      // Phase 2: Branch hashes
      const localTrees = new Map<string, MerkleTree>();
      for (const table of tables) {
        const tree = treeCache.get(table);
        if (tree) {
          localTrees.set(table, tree);
        }
      }

      if (localTrees.size > 0) {
        const phase2 = await this.phase2_exchangeBranches(localTrees, phase1.rootHashes);
        totalBytesUsed += phase2.bytesUsed;

        // Phase 3: Sync changed records
        for (const [table] of phase2.changedTables) {
          if (!BandwidthManager.canProceed(10 * 1024)) {
            logger.warn('Insufficient bandwidth for Phase 3');
            break;
          }

          // In real implementation, would use branch hashes to identify exact changed records
          // For now, using placeholder
          const phase3 = await this.phase3_syncChangedRecords(table, [], options?.organizationId);
          totalBytesUsed += phase3.bytesUsed;
          recordsSynced += phase3.recordsSynced;
        }
      }
    } catch (error) {
      logger.error('Sync failed', error);
    }

    const endTime = performance.now();
    const duration = endTime - startTime;

    logger.info('Sync complete', {
      tablesProcessed,
      recordsSynced,
      bandwidthMB: (totalBytesUsed / 1024 / 1024).toFixed(2),
      durationSeconds: (duration / 1000).toFixed(2)
    });

    return {
      tablesProcessed,
      recordsSynced,
      totalBytesUsed,
      remainingBandwidth: BandwidthManager.getRemainingBandwidth(),
    };
  }
}

// Export for global access
export const bandwidthSync = BandwidthConstrainedSync;
